import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import type { VideoLibraryAnalyticsProps } from "@/content-hub/analytics/video-library-events";
import { trackContentPageOpenSharedUrl } from "@/content-hub/analytics/video-library-events";
import logger from "@/utils/logger";

import { resolveSectionIdFromElement } from "./resolve-section-id";

type UseAutoPlayFromUrlProps = {
  videoId: string | undefined;
  /** Query params from older links used full embed URL as videoId; match those too. */
  legacyEmbedUrl?: string;
  isEditorEnabled: boolean;
  containerRef: RefObject<HTMLElement | null>;
  openPlayer: () => void;
  /**
   * Mirrors LaunchDarkly `contentHubDirectVideoSharing` for this card. Callers that
   * suppress the whole stack (e.g. single-video legacy) pass `false` here.
   */
  directVideoSharingEnabled: boolean;
  /**
   * Single-video block: same Craft node id used in settings share URLs
   * (`data-section-id` on the Video primitive). Grid cards omit this and resolve from DOM.
   */
  shareSectionId?: string | null;
  analytics: VideoLibraryAnalyticsProps;
};

function queryVideoIdMatches(
  qp: string | null,
  stableVideoId: string | undefined,
  legacyEmbedUrl?: string
): boolean {
  if (!qp || !stableVideoId) return false;
  if (qp === stableVideoId) return true;
  if (!legacyEmbedUrl) return false;
  try {
    const normalized = qp.includes("%")
      ? decodeURIComponent(qp.replace(/\+/g, " "))
      : qp;
    return normalized === legacyEmbedUrl || qp === legacyEmbedUrl;
  } catch {
    return qp === legacyEmbedUrl;
  }
}

/**
 * Auto-scrolls to the section and triggers the player when the page is loaded
 * with `?videoId=<id>&sectionId=<id>` query params produced by the video card
 * play handler. The check runs once per mount per matching card.
 */
export const useAutoPlayFromUrl = ({
  videoId,
  legacyEmbedUrl,
  isEditorEnabled,
  containerRef,
  openPlayer,
  directVideoSharingEnabled,
  shareSectionId,
  analytics,
}: UseAutoPlayFromUrlProps) => {
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (typeof window === "undefined") return;
    if (!directVideoSharingEnabled || isEditorEnabled || !videoId) {
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const sectionId =
      shareSectionId?.trim() || resolveSectionIdFromElement(element);
    if (!sectionId) {
      return;
    }

    let qpVideoId: string | null = null;
    let qpSectionId: string | null = null;

    try {
      const params = new URLSearchParams(window.location.search);
      qpVideoId = params.get("videoId");
      qpSectionId = params.get("sectionId");
    } catch (error) {
      logger.error(error, {
        location: "content-hub/use-auto-play-from-url:parse-url",
      });
      return;
    }

    if (
      qpSectionId !== sectionId ||
      !queryVideoIdMatches(qpVideoId, videoId, legacyEmbedUrl)
    ) {
      return;
    }

    hasAutoOpened.current = true;
    trackContentPageOpenSharedUrl(analytics, "videoId");

    // Two RAFs guarantee the browser has performed at least one layout pass
    // before we measure/scroll, so the section sits at its final offset even
    // when sibling content (images, recordings) is still settling.
    let activeFrameId = requestAnimationFrame(() => {
      activeFrameId = requestAnimationFrame(() => {
        try {
          // Scroll the actual video card — not only the section shell. Blocks like
          // "Video with title and text" place copy / ExternalLink CTAs above or beside
          // the embed; scrolling `section` to `start` leaves the YouTube card off-screen.
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        } catch (error) {
          logger.error(error, {
            location: "content-hub/use-auto-play-from-url:scroll",
          });
        }
        openPlayer();
      });
    });

    return () => cancelAnimationFrame(activeFrameId);
  }, [
    directVideoSharingEnabled,
    videoId,
    legacyEmbedUrl,
    isEditorEnabled,
    containerRef,
    openPlayer,
    shareSectionId,
    analytics,
  ]);
};
